import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment";

import manhole from "../../markerIcons/manhole.svg";
import hydrant from "../../markerIcons/hydrant.svg";
import valve from "../../markerIcons/valve.svg";
import sign from "../../markerIcons/trafficSign.svg";
import watermeter from "../../markerIcons/watermeter.svg";
import waterline from "../../markerIcons/waterline.svg";
import sewerline from "../../markerIcons/sewerline.svg";

import "./styles/overrideStyle.css";

// Map search template field names to possible API column names (e.g. SQL returns different casing/underscores)
const FIELD_NAME_ALTERNATES = {
  // API / SP may omit FacilityID; human-readable IDs (e.g. MH-704) often live on Client*ID fields
  FacilityID: [
    "Facility_Id",
    "FacilityId",
    "facilityId",
    "ClientManholeID",
    "ClientHydrantID",
    "ClientValveID",
    "ClientSignID",
    "ClientWaterMeterID",
    "ClientWaterLineID",
    "ClientSewerLineID",
  ],
  SubDivisionName: ["SubdivisionName", "Subdivisionname", "subDivisionName"],
};

// Normalized asset type -> Client ID field name (API may return "Traffic Sign", key is ClientSignID)
const CLIENT_ID_FIELD_BY_NORMALIZED_TYPE = {
  hydrant: "ClientHydrantID",
  manhole: "ClientManholeID",
  valve: "ClientValveID",
  trafficsign: "ClientSignID",
  watermeter: "ClientWaterMeterID",
  waterline: "ClientWaterLineID",
  sewerline: "ClientSewerLineID",
};

function SearchModal({
  data,
  serviceData: initialServiceData,
  assetType,
  user,
  cancel,
  SelectFromSearch,
  checkForHighlightedMarkers,
}) {
  // ---------------------------
  //   State Variables
  // ---------------------------
  const [modal] = useState(true); // not used in code, but kept for reference
  const [makeDraggable, setMakeDraggable] = useState(false);
  const [assetSearchValue, setAssetSearchValue] = useState("");
  const [assetTypeSearch, setAssetTypeSearch] = useState("");
  const [assetField, setAssetField] = useState("");
  const [availableAssets] = useState([
    "Hydrant",
    "Manhole",
    "TrafficSign",
    "WaterMeter",
    "Valve",
    "WaterLine",
    "SewerLine",
  ]);

  // Mapping of asset type -> columns
  const [availableAssetsColumns, setAvailableAssetsColumns] = useState({});
  const [availableAssetsFields, setAvailableAssetsFields] = useState([]);

  const [filteredData, setFilteredData] = useState([]);
  const [serviceData, setServiceData] = useState(initialServiceData);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);

  // Per-marker opacity before highlight (ref avoids setState in highlight → infinite effect loop)
  const markerOpacityBeforeHighlightRef = useRef(new Map());

  // On mount, we’ll load saved localStorage items, fetch asset types, etc.
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Fetch the asset type columns
        const assetTypeRes = await axios.get("/api/getAssetTypes");
        if (mounted && assetTypeRes.data) {
          setAvailableAssetsColumns(assetTypeRes.data.asset);
        }

        // 2) Retrieve local storage for search: assetSearch, assetTypeSearch, assetFieldSearch
        const localAssetSearch = localStorage.getItem("assetSearch") || "";
        const localTypeSearch = localStorage.getItem("assetTypeSearch") || "";
        const localFieldSearch = localStorage.getItem("assetFieldSearch") || "";

        // 3) If we have an asset type in localStorage, filter by that
        if (localTypeSearch) {
          const filtered = initialServiceData.filter(
            (f) =>
              f.assetType &&
              f.assetType.toLowerCase().includes(localTypeSearch.toLowerCase())
          );
          setFilteredData(filtered);
          setAssetTypeSearch(localTypeSearch);

          // If we have columns for that type, set them
          const newFields = assetTypeRes.data.asset[localTypeSearch] || [];
          setAvailableAssetsFields(newFields);
        }

        // 4) If we have an asset field in localStorage, filter further
        if (localFieldSearch) {
          setAssetField(localFieldSearch);
        }

        // 5) Finally, do a text search using localAssetSearch
        if (localAssetSearch) {
          // If it's "DaysSinceLastInspection"
          if (localFieldSearch === "DaysSinceLastInspection") {
            changeDaysSinceLastInspection(localAssetSearch, initialServiceData);
            setAssetSearchValue(localAssetSearch);
          } else {
            // do normal search
            handleChangeValue(localAssetSearch, localTypeSearch, localFieldSearch);
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error("Error in SearchModal initial load:", err);
        setIsLoading(false);
      }
    })();

    // Listen for resize to update mobile vs. desktop
    const resizeListener = (evt) => {
      const w = evt.target.innerWidth;
      if (w < 767) {
        setMakeDraggable(true);
        setIsMobile(true);
      } else {
        setMakeDraggable(false);
        setIsMobile(false);
      }
    };
    window.addEventListener("resize", resizeListener, true);

    return () => {
      mounted = false;
      window.removeEventListener("resize", resizeListener, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Similar to componentWillReceiveProps: watch for changes in user or serviceData
  useEffect(() => {
    setServiceData(initialServiceData);
  }, [initialServiceData]);

  // ----------------------------
  //   Helper / Utility Functions
  // ----------------------------

  // Normalize asset type for comparison (API may return "Traffic Sign", "Water Meter"; UI uses "TrafficSign", "WaterMeter")
  const normalizeAssetType = (type) => {
    if (type == null) return "";
    return String(type).replace(/\s+/g, "").toLowerCase();
  };

  // Get field value from item, trying template name and common API alternates (e.g. SubdivisionName vs SubDivisionName, Facility_Id vs FacilityID)
  const getFieldValue = (item, fieldName) => {
    if (item[fieldName] != null && item[fieldName] !== "") return item[fieldName];
    const alternates = FIELD_NAME_ALTERNATES[fieldName];
    if (alternates) {
      for (const alt of alternates) {
        if (item[alt] != null && item[alt] !== "") return item[alt];
      }
    }
    // Try case-insensitive key match (API may return SubdivisionName when template has SubDivisionName)
    const fieldLower = fieldName.toLowerCase();
    const keyMatch = Object.keys(item).find((k) => k.toLowerCase() === fieldLower);
    if (keyMatch != null && item[keyMatch] != null && item[keyMatch] !== "") return item[keyMatch];
    return undefined;
  };

  // Main logic to highlight or remove highlight from markers (no setState — avoids effect ↔ callback dependency loop)
  const highlightMarker = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      const parentEl = element.parentNode;
      if (!markerOpacityBeforeHighlightRef.current.has(id)) {
        markerOpacityBeforeHighlightRef.current.set(id, parentEl.style.opacity || "");
      }
      parentEl.style.opacity = 1;
      element.classList.add("img-icon-selected");
    }
  }, []);

  const removeHighlightMarker = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      const parentEl = element.parentNode;
      const prev = markerOpacityBeforeHighlightRef.current.get(id);
      if (prev !== undefined) {
        parentEl.style.opacity = prev;
        markerOpacityBeforeHighlightRef.current.delete(id);
      } else {
        parentEl.style.opacity = "";
      }
      element.classList.remove("img-icon-selected");
    }
  }, []);

  // Searching for normal text fields
  const handleChangeValue = useCallback(
    (searchValue, typeSearch, fieldName) => {
      if (!searchValue) {
        setAssetSearchValue("");
        localStorage.setItem("assetSearch", "");
        // Reset with the typeSearch and fieldName alone
        handleChangeAssetField({ target: { value: fieldName } }, typeSearch, "");
        return;
      }

      setAssetSearchValue(searchValue);
      localStorage.setItem("assetSearch", searchValue);

      const newFiltered = filterDataBySearch(
        serviceData,
        typeSearch,
        fieldName,
        searchValue
      );
      setFilteredData(newFiltered);
    },
    [serviceData]
  );

  // The actual filter function that checks assetTypeSearch, assetField, etc.
  const filterDataBySearch = (dataList, typeSearch, fieldName, searchVal) => {
    const normalized = searchVal.toLowerCase();
    const typeNorm = normalizeAssetType(typeSearch);

    return dataList.filter((item) => {
      const itemTypeNorm = normalizeAssetType(item.assetType);
      const typeMatches = typeNorm ? itemTypeNorm === typeNorm : true;

      // If we have both an assetType and a field
      if (typeSearch && fieldName) {
        const fieldVal = getFieldValue(item, fieldName);
        return (
          typeMatches &&
          fieldVal != null &&
          String(fieldVal).toLowerCase().includes(normalized)
        );
      }
      // If we only have an assetType
      if (typeSearch && !fieldName) {
        if (typeMatches) {
          return Object.values(item).some(
            (val) => val != null && val !== "" && String(val).toLowerCase().includes(normalized)
          );
        }
        return false;
      }
      // If we only have a field
      if (!typeSearch && fieldName) {
        const fieldVal = getFieldValue(item, fieldName);
        return (
          fieldVal != null &&
          String(fieldVal).toLowerCase().includes(normalized)
        );
      }
      // Otherwise, search across everything
      return Object.values(item).some(
        (val) => val != null && val !== "" && String(val).toLowerCase().includes(normalized)
      );
    });
  };

  // Reaction to changes in filteredData: highlight/unhighlight markers
  useEffect(() => {
    // 1) Unhighlight all markers first
    serviceData.forEach((x) => removeHighlightMarker(x.GlobalID));
    localStorage.removeItem("highlightedMarkers");
    // 2) highlight those in filteredData
    filteredData.forEach((x) => highlightMarker(x.GlobalID));
    localStorage.setItem(
      "highlightedMarkers",
      filteredData.map((f) => f.GlobalID).toString()
    );
    // 3) callback to parent
    checkForHighlightedMarkers(filteredData.map((f) => f.GlobalID));
  }, [filteredData, serviceData, checkForHighlightedMarkers, removeHighlightMarker, highlightMarker]);

  // Changing the selected asset type
  const handleChangeAssetType = (e) => {
    const val = e.target.value;
    if (!val) {
      // If empty, reset everything
      setFilteredData([]);
      setAssetSearchValue("");
      setAssetTypeSearch("");
      setAvailableAssetsFields([]);
      localStorage.setItem("assetTypeSearch", "");
      return;
    }
    setAssetTypeSearch(val);
    localStorage.setItem("assetTypeSearch", val);

    // Filter data by that type (normalize so "Traffic Sign" matches "TrafficSign", etc.)
    const valNorm = normalizeAssetType(val);
    const newFiltered = serviceData.filter((f) => normalizeAssetType(f.assetType) === valNorm);
    setFilteredData(newFiltered);
    setAssetSearchValue("");
    setAssetField("");
    // Set possible columns for that type
    const newFields = availableAssetsColumns[val] || [];
    setAvailableAssetsFields(newFields);
  };

  // Changing the selected field for search
  const handleChangeAssetField = (e, existingType = assetTypeSearch, textVal = assetSearchValue) => {
    const val = e.target.value;
    if (!val) {
      // Reset
      setFilteredData([]);
      setAssetSearchValue("");
      setAssetField("");
      localStorage.setItem("assetFieldSearch", "");

      // Re-run the type filter alone
      handleChangeAssetType({ target: { value: existingType } });
      return;
    }
    setAssetField(val);
    localStorage.setItem("assetFieldSearch", val);

    if (existingType) {
      // Filter data by type + field (normalize asset type; use getFieldValue for API column name variants)
      const existingTypeNorm = normalizeAssetType(existingType);
      const newFiltered = serviceData.filter((f) => {
        if (normalizeAssetType(f.assetType) !== existingTypeNorm) return false;
        const fieldVal = getFieldValue(f, val);
        if (textVal) {
          return (
            fieldVal != null &&
            String(fieldVal).toLowerCase().includes(textVal.toLowerCase())
          );
        }
        return fieldVal != null && fieldVal !== "";
      });
      setFilteredData(newFiltered);
      setAssetSearchValue("");
    }
  };

  // For DaysSinceLastInspection specifically
  const changeDaysSinceLastInspection = (val, dataList = serviceData) => {
    if (!val) {
      setAssetSearchValue("");
      localStorage.setItem("assetSearch", "");
      return;
    }
    const numVal = parseInt(val, 10);
    setAssetSearchValue(val);
    localStorage.setItem("assetSearch", val);

    const typeNorm = normalizeAssetType(assetTypeSearch);
    const newFiltered = dataList.filter((f) => {
      if (assetTypeSearch && normalizeAssetType(f.assetType) === typeNorm && f.DaysSinceLastInspection >= numVal) {
        return true;
      }
      if (!assetTypeSearch && f.DaysSinceLastInspection >= numVal) {
        return true;
      }
      return false;
    });
    setFilteredData(newFiltered);
  };

  // For freeSolo suggestions (previous values); uses getFieldValue so API column name variants are found
  const getPreviousValues = (optionArray, name) => {
    if (!optionArray || !name) return [];
    if (name.toLowerCase().includes("date")) {
      return [
        ...new Set(
          optionArray
            .map((col) => {
              const v = getFieldValue(col, name);
              return v && String(v).split("T")[0].toLowerCase();
            })
            .filter((x) => x)
        ),
      ];
    } else {
      return [
        ...new Set(
          optionArray
            .map((col) => {
              const v = getFieldValue(col, name);
              return v != null && v !== "" ? String(v).toLowerCase() : null;
            })
            .filter((x) => x)
        ),
      ].map((opt) => opt.charAt(0).toUpperCase() + opt.slice(1));
    }
  };

  // Convert array of objects to CSV and let user download
  const downloadFilteredData = async () => {
    if (!filteredData.length) return;
    try {
      const csv = await convertArrayOfObjectsToCSV(filteredData);
      if (!csv) return;

      let finalStr = csv;
      const filename = "searchexport.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        finalStr = "data:text/csv;charset=utf-8," + csv;
      }
      const encoded = encodeURI(finalStr);

      const link = document.createElement("a");
      link.setAttribute("href", encoded);
      link.setAttribute("download", filename);
      link.click();
    } catch (err) {
      console.error("Error downloading filtered data:", err);
    }
  };

  // Build CSV string for the chosen asset type
  const convertArrayOfObjectsToCSV = async (csvData) => {
    if (!csvData.length) return null;
    // fetch field definitions for the current assetTypeSearch
    const { data: assetRes } = await axios.get(`/api/assetFields/${assetTypeSearch}`);
    const assetDefs = assetRes.asset;
    if (!assetDefs) return null;

    const fieldNames = assetDefs.inputObjectArray
      .filter((f) => f.display)
      .map((f) => f.name);
    fieldNames.push("DaysSinceLastInspection", "LastInspectionDate", "CurrentRating");

    const columnDelimiter = ",";
    const lineDelimiter = "\n";

    // only keep columns that match fieldNames
    const keys = Object.keys(csvData[0]).filter((k) => fieldNames.includes(k));

    let result = "";
    // first row: column headers
    result += keys.join(columnDelimiter);
    result += lineDelimiter;

    csvData.forEach((row) => {
      let ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) result += columnDelimiter;
        if (key === "assetType") {
          // map to label
          const mapped = row[key] === "Hydrant"
            ? "Hydrant"
            : row[key] === "Manhole"
            ? "Manhole"
            : row[key] === "Valve"
            ? "Valve"
            : row[key] === "WaterLine"
            ? "WaterLine"
            : row[key] === "SewerLine"
            ? "SewerLine"
            : row[key] === "TrafficSign"
            ? "Traffic Sign"
            : row[key];
          result += mapped || "";
        } else if (key.toLowerCase().includes("date")) {
          // check for null
          if (!row[key]) {
            result += "";
          } else {
            result += moment(row[key]).format("YYYY-MM-DD");
          }
        } else if (row[key] === null) {
          result += "";
        } else {
          result += row[key];
        }
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  };

  // Clears the search + unhighlights
  const clearFilteredDataIcons = () => {
    serviceData.forEach((x) => removeHighlightMarker(x.GlobalID));
    localStorage.removeItem("highlightedMarkers");
    setFilteredData([]);
    setAssetSearchValue("");
    setAssetTypeSearch("");
    setAvailableAssetsFields([]);
    localStorage.setItem("assetTypeSearch", "");
    localStorage.setItem("assetFieldSearch", "");
    localStorage.setItem("assetSearch", "");
    checkForHighlightedMarkers([]);
  };

  // Closing the modal
  const handleClose = () => {
    // unhighlight all markers
    serviceData.forEach((f) => removeHighlightMarker(f.GlobalID));
    localStorage.removeItem("highlightedMarkers");
    cancel();
  };

  // -----------
  //  Rendering
  // -----------
  return (
    <div className="AddSearchToMapContainer">
      <div style={{ textAlign: "right", position: "absolute", left: "90%", zIndex: 1, top: "10px" }}>
        <i
          className="fa fa-times"
          style={{ color: "red", fontSize: "27px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          aria-hidden
        />
      </div>
      {isLoading ? (
        <div className="outerContainer" style={{ padding: "11px 20px 27px 20px" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </div>
        </div>
      ) : (
        <div className="outerContainer" style={{ padding: "11px 20px 27px 20px" }}>
          <h2 className="containerHeader">Search For An Asset</h2>
          {/* 1) Asset Type */}
          <div className="assetRowSearch">
            <TextField
              autoComplete="off"
              select
              label="Asset Type"
              variant="outlined"
              InputLabelProps={{ shrink: true, style: { color: "#4285f4" } }}
              onMouseDown={(e) => e.preventDefault()}
              InputProps={{ style: { minWidth: isMobile ? "91vw" : "245px", marginBottom: "10px" } }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                },
              }}
              value={assetTypeSearch}
              onChange={handleChangeAssetType}
            >
              <MenuItem value="" hidden>
                Select an Asset
              </MenuItem>
              {availableAssets.map((obj) => (
                <MenuItem key={obj} value={obj}>
                  {obj}
                </MenuItem>
              ))}
            </TextField>
          </div>

          {/* 2) Asset Field */}
          <div className="assetRowSearch">
            <TextField
              autoComplete="off"
              select
              label="Asset Field"
              variant="outlined"
              placeholder="Select a Field"
              InputLabelProps={{ shrink: true, style: { color: "#4285f4" } }}
              onMouseDown={(e) => e.preventDefault()}
              InputProps={{ style: { minWidth: isMobile ? "91vw" : "245px", marginBottom: "10px" } }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                },
              }}
              value={assetField}
              onChange={(e) => handleChangeAssetField(e)}
            >
              <MenuItem value="" hidden>
                Select a Field
              </MenuItem>
              {availableAssetsFields.map((obj) =>
                obj.name ? (
                  <MenuItem key={obj.name} value={obj.name}>
                    {obj.displayName}
                  </MenuItem>
                ) : (
                  <MenuItem key={obj} value={obj}>
                    {obj}
                  </MenuItem>
                )
              )}
            </TextField>
          </div>

          {/* 3) Search Input */}
          <div className="assetRowSearch">
            {assetField !== "DaysSinceLastInspection" ? (
              <Autocomplete
                freeSolo
                style={{ width: "100%" }}
                options={getPreviousValues(filteredData, assetField)}
                value={assetSearchValue}
                // MUI freeSolo often calls onChange(null) while typing/blur — that was clearing the search and results
                onChange={(e, newValue) => {
                  if (newValue != null && newValue !== "") {
                    handleChangeValue(newValue, assetTypeSearch, assetField);
                  }
                }}
                onInputChange={(e, newValue, reason) => {
                  if (reason === "input") {
                    handleChangeValue(newValue, assetTypeSearch, assetField);
                  } else if (reason === "clear") {
                    handleChangeValue("", assetTypeSearch, assetField);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search"
                    autoComplete="off"
                    variant="outlined"
                    placeholder="Select a value or Enter a value"
                    InputLabelProps={{ shrink: true, style: { color: "#4285f4" } }}
                    className="inputAssetSearchMainContainer"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                      className: "inputAssetSearch",
                      style: {
                        minWidth: isMobile ? "91vw" : "245px",
                        marginBottom: "10px",
                      },
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                autoComplete="off"
                label="Search"
                type="number"
                variant="outlined"
                placeholder="Enter a value"
                InputLabelProps={{ shrink: true, style: { color: "#4285f4" } }}
                InputProps={{ style: { minWidth: isMobile ? "91vw" : "245px", marginBottom: "10px" } }}
                value={assetSearchValue}
                onChange={(e) => changeDaysSinceLastInspection(e.target.value)}
              />
            )}
          </div>

          {/* 4) Buttons (Clear & Download) */}
          <div style={{ textAlign: "center", marginBottom: "10px" }}>
            <div style={{ width: "90%", margin: "auto", display: "flex", justifyContent: "space-around" }}>
              {filteredData.length > 0 && (
                <Button variant="contained" color="primary" onClick={clearFilteredDataIcons}>
                  Clear
                </Button>
              )}
              <Button variant="contained" color="primary" onClick={downloadFilteredData}>
                Download
              </Button>
            </div>
          </div>

          {/* 5) Search Results */}
          <div style={isMobile ? { overflow: "auto", height: "100%" } : { overflow: "auto", height: "75%" }}>
            {assetSearchValue && filteredData.length > 0 ? (
              filteredData.map((x, i) => (
                <div className="assetCard" key={x.GlobalID}>
                  <div
                    className="assetCardText"
                    role="button"
                    tabIndex={0}
                    onClick={() => SelectFromSearch(x)}
                  >
                    <div>
                      <img
                        src={
                          {
                            Hydrant: hydrant,
                            Manhole: manhole,
                            Valve: valve,
                            TrafficSign: sign,
                            WaterMeter: watermeter,
                            WaterLine: waterline,
                            SewerLine: sewerline,
                          }[String(x.assetType || "").replace(/\s+/g, "")] || sign
                        }
                        alt={x.assetType}
                        className={String(x.assetType || "").replace(/\s+/g, "") === "Manhole" ? "leaflet-svg-icon-sewer" : ""}
                        style={{ width: "20px", height: "20px" }}
                      />
                    </div>
                    {!assetField || getFieldValue(x, assetField) == null || getFieldValue(x, assetField) === "" ? (
                      <div>
                        <p>
                          <span style={{ color: "blue" }}>Id:</span>{" "}
                          {x[CLIENT_ID_FIELD_BY_NORMALIZED_TYPE[normalizeAssetType(x.assetType)] || `Client${x.assetType}ID`]}
                        </p>
                        <p>
                          <span style={{ color: "blue" }}>Last Edited:</span>{" "}
                          {moment(new Date(x.Last_Edited_Date)).format("YYYY-MM-DD")}
                        </p>
                        <p>
                          <span style={{ color: "blue" }}>Edited By:</span> {x.Last_Editor}
                        </p>
                      </div>
                    ) : (
                      <div style={{ minWidth: "160px" }}>
                        <p>
                          <span style={{ color: "blue" }}>Id:</span> {x[CLIENT_ID_FIELD_BY_NORMALIZED_TYPE[normalizeAssetType(x.assetType)] || `Client${x.assetType}ID`]}
                        </p>
                        <p>
                          <span style={{ color: "blue" }}>
                            {
                              availableAssetsFields
                                .filter((obj) =>
                                  obj.name
                                    ? obj.name === assetField
                                    : obj === assetField
                                )
                                .map((obj) => (obj.name ? obj.displayName : obj))[0]
                            }
                            :{" "}
                          </span>
                          {assetField.toLowerCase().includes("date")
                            ? moment(new Date(getFieldValue(x, assetField))).format("YYYY-MM-DD")
                            : getFieldValue(x, assetField)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", width: "100%" }}>No Assets Found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchModal;
